import React, { useState } from "react";
import "./ContactUs.css";
import { useAppData } from "../contexts/AppDataContext";

const BASE = process.env.PUBLIC_URL || "/landing-page"; // ==> "/landing" in your dev env

export const ICONS = {
    whatsapp: `${BASE}/assets/whatsapp.png`,
    phone: `${BASE}/assets/phone.png`,
    mail: `${BASE}/assets/email.png`,
    twitter: `${BASE}/assets/twitter.png`,
    instagram: `${BASE}/assets/instagram.png`,
    facebook: `${BASE}/assets/Facebook .png`,
    tiktok: `${BASE}/assets/tiktok.png`,
};

/* Toast component for notifications */
function Toast({ open, message, type = "success", onClose, duration = 2500 }) {
    React.useEffect(() => {
        if (!open) return;
        const t = setTimeout(onClose, duration);
        return () => clearTimeout(t);
    }, [open, duration, onClose]);

    if (!open) return null;
    return (
        <div className={`toast toast--${type}`} role="status" aria-live="polite" dir="rtl">
            <span className="toast__icon" aria-hidden>{type === "success" ? "✓" : "!"}</span>
            <span className="toast__text">{message}</span>
            <button className="toast__close" onClick={onClose} aria-label="إغلاق">×</button>
        </div>
    );
}

export default function ContactUs() {
    const { appData, loading: appDataLoading } = useAppData();

    const [formData, setFormData] = useState({
        firstName: "",
        phoneNumber: "",
        email: "",
        subject: "",
        howCanWeHelp: ""
    });
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ open: false, type: "success", message: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = "الاسم الأول مطلوب";
        }

        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = "رقم الهاتف مطلوب";
        } else if (!/^\+?\d[\d\s-]{7,}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "رقم الهاتف غير صالح";
        }

        if (!formData.email.trim()) {
            newErrors.email = "البريد الإلكتروني مطلوب";
        } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
            newErrors.email = "البريد الإلكتروني غير صالح";
        }

        if (!formData.subject.trim()) {
            newErrors.subject = "الموضوع مطلوب";
        }

        if (!formData.howCanWeHelp.trim()) {
            newErrors.howCanWeHelp = "الوصف مطلوب";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            const response = await fetch('/api/contact-requests', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                throw new Error(errorText || `HTTP ${response.status}`);
            }

            setToast({
                open: true,
                type: "success",
                message: "تم إرسال رسالتك بنجاح! سنتواصل معك قريباً 👌"
            });

            // Reset form
            setFormData({
                firstName: "",
                phoneNumber: "",
                email: "",
                subject: "",
                howCanWeHelp: ""
            });
            setErrors({});

        } catch (error) {
            setToast({
                open: true,
                type: "error",
                message: "حدث خطأ أثناء إرسال الرسالة. حاول مجدداً."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="contact" dir="ltr" aria-labelledby="contact-title">
            <div className="contact__head">
                <h2 id="contact-title">تواصل معنا</h2>
                <p>
                    نحن في تحرّر نؤمن بأن التواصل هو أساس بناء علاقات ناجحة. نحن هنا للاستماع إليك ومساعدتك.
                </p>
            </div>

            <div className="contact__card" role="group" aria-label="نموذج التواصل ومعلومات الاتصال">
                {/* يسار: النموذج */}
                <form className="contact__form" onSubmit={handleSubmit} dir="rtl">
                    <header className="contact__form-head">
                        <h3>أخبرنا كيف يمكننا المساعدة</h3>
                        <p>لا تتردد في التواصل مع فريق الدعم الفني لدينا للإجابة على جميع أسئلتك.</p>
                    </header>

                    <label className="field">
                        <span className="field__label">الاسم الأول</span>
                        <input
                            className={`field__input ${errors.firstName ? "is-invalid" : ""}`}
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="شيماء"
                            aria-invalid={!!errors.firstName}
                            aria-describedby={errors.firstName ? "err-firstName" : undefined}
                        />
                        {errors.firstName && (
                            <p className="field__error" id="err-firstName">
                                <span className="field__error-ico">!</span>
                                {errors.firstName}
                            </p>
                        )}
                    </label>

                    <label className="field">
                        <span className="field__label">رقم الهاتف</span>
                        <input
                            className={`field__input ${errors.phoneNumber ? "is-invalid" : ""}`}
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleInputChange}
                            placeholder="+965 - 00 0000 0000"
                            inputMode="tel"
                            aria-invalid={!!errors.phoneNumber}
                            aria-describedby={errors.phoneNumber ? "err-phoneNumber" : undefined}
                        />
                        {errors.phoneNumber && (
                            <p className="field__error" id="err-phoneNumber">
                                <span className="field__error-ico">!</span>
                                {errors.phoneNumber}
                            </p>
                        )}
                    </label>

                    <label className="field">
                        <span className="field__label">البريد الإلكتروني</span>
                        <input
                            className={`field__input ${errors.email ? "is-invalid" : ""}`}
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Moh@untitledui.com"
                            aria-invalid={!!errors.email}
                            aria-describedby={errors.email ? "err-email" : undefined}
                        />
                        {errors.email && (
                            <p className="field__error" id="err-email">
                                <span className="field__error-ico">!</span>
                                {errors.email}
                            </p>
                        )}
                    </label>

                    <label className="field">
                        <span className="field__label">الموضوع</span>
                        <div className="select">
                            <select
                                className={`field__input field__select ${errors.subject ? "is-invalid" : ""}`}
                                name="subject"
                                value={formData.subject}
                                onChange={handleInputChange}
                                aria-invalid={!!errors.subject}
                                aria-describedby={errors.subject ? "err-subject" : undefined}
                            >
                                <option value="" disabled>اختر موضوعًا…</option>
                                <option value="مشكلة تقنية">مشكلة تقنية</option>
                                <option value="الاشتراكات والمدفوعات">الاشتراكات والمدفوعات</option>
                                <option value="اقتراح/ملاحظة">اقتراح/ملاحظة</option>
                                <option value="أخرى">أخرى</option>
                            </select>
                            <span className="select__chev">▾</span>
                        </div>
                        {errors.subject && (
                            <p className="field__error" id="err-subject">
                                <span className="field__error-ico">!</span>
                                {errors.subject}
                            </p>
                        )}
                    </label>

                    <label className="field field--textarea">
                        <span className="field__label">كيف يمكننا مساعدتك؟</span>
                        <textarea
                            className={`field__input ${errors.howCanWeHelp ? "is-invalid" : ""}`}
                            name="howCanWeHelp"
                            value={formData.howCanWeHelp}
                            onChange={handleInputChange}
                            rows="5"
                            placeholder="اكتب رسالتك هنا…"
                            aria-invalid={!!errors.howCanWeHelp}
                            aria-describedby={errors.howCanWeHelp ? "err-howCanWeHelp" : undefined}
                        ></textarea>
                        {errors.howCanWeHelp && (
                            <p className="field__error" id="err-howCanWeHelp">
                                <span className="field__error-ico">!</span>
                                {errors.howCanWeHelp}
                            </p>
                        )}
                    </label>

                    <button
                        className="contact__submit"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "جارٍ الإرسال…" : "تواصَل معنا"}
                    </button>
                </form>

                {/* يمين: معلومات التواصل */}
                <aside className="contact__info" aria-label="معلومات التواصل">
                    {/* خلفية دوائر (اختياري) */}
                    <img className="contact__rings" src={`${BASE}/assets/contact-rings.png`}
                        alt="" aria-hidden="true" />

                    <h3 className="contact__info-title">معلومات التواصل</h3>
                    <p className="contact__info-sub">تواصل معنا دائمًا بخصوص رحلتك في التعافي…</p>

                    <ul className="contact__list">
                        {appData?.whatsappNumber && (
                            <li className="contact__row">
                                <span className="ico">
                                    <img src={ICONS.whatsapp} alt="" aria-hidden="true" />
                                </span>
                                <div>
                                    <div className="contact__label">واتساب</div>
                                    <a href={`https://wa.me/${appData.whatsappNumber.replace(/[^0-9]/g, '')}`} className="contact__value">
                                        {appData.whatsappNumber}
                                    </a>
                                </div>
                            </li>
                        )}

                        {appData?.phoneNumber && (
                            <li className="contact__row">
                                <span className="ico">
                                    <img src={ICONS.phone} alt="" aria-hidden="true" />
                                </span>
                                <div>
                                    <div className="contact__label">الهاتف</div>
                                    <a href={`tel:${appData.phoneNumber}`} className="contact__value">
                                        {appData.phoneNumber}
                                    </a>
                                </div>
                            </li>
                        )}

                        {appData?.email && (
                            <li className="contact__row">
                                <span className="ico">
                                    <img src={ICONS.mail} alt="" aria-hidden="true" />
                                </span>
                                <div>
                                    <div className="contact__label">البريد الإلكتروني</div>
                                    <a href={`mailto:${appData.email}`} className="contact__value">
                                        {appData.email}
                                    </a>
                                </div>
                            </li>
                        )}

                        {/* Show loading state */}
                        {appDataLoading && (
                            <li className="contact__row">
                                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                    جارٍ تحميل معلومات التواصل...
                                </div>
                            </li>
                        )}

                        {/* Show empty state if no contact info */}
                        {!appDataLoading && !appData?.whatsappNumber && !appData?.phoneNumber && !appData?.email && (
                            <li className="contact__row">
                                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                    معلومات التواصل غير متوفرة حالياً
                                </div>
                            </li>
                        )}
                    </ul>

                    <div className="contact__social" aria-label="تابعنا">
                        {appData?.tiktokLink && (
                            <a aria-label="TikTok" href={appData.tiktokLink} target="_blank" rel="noopener noreferrer" className="soc1">
                                <div className="imgContainer">
                                    <img className="soc__img" src={ICONS.tiktok} alt="" />
                                </div>
                            </a>
                        )}

                        {appData?.facebookLink && (
                            <a aria-label="Facebook" href={appData.facebookLink} target="_blank" rel="noopener noreferrer" className="soc2">
                                <div className="imgContainer">
                                    <img className="soc__img" src={ICONS.facebook} alt="" />
                                </div>
                            </a>
                        )}

                        {appData?.instagramLink && (
                            <a aria-label="Instagram" href={appData.instagramLink} target="_blank" rel="noopener noreferrer" className="soc1">
                                <div className="imgContainer">
                                    <img className="soc__img" src={ICONS.instagram} alt="" />
                                </div>
                            </a>
                        )}

                        {appData?.twitterLink && (
                            <a aria-label="Twitter" href={appData.twitterLink} target="_blank" rel="noopener noreferrer" className="soc2">
                                <div className="imgContainer">
                                    <img className="soc__img" src={ICONS.twitter} alt="" />
                                </div>
                            </a>
                        )}

                        {/* Show message if no social links */}
                        {!appDataLoading && !appData?.tiktokLink && !appData?.facebookLink && !appData?.instagramLink && !appData?.twitterLink && (
                            <div style={{ padding: '10px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                                روابط التواصل الاجتماعي غير متوفرة حالياً
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            <Toast
                open={toast.open}
                type={toast.type}
                message={toast.message}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                duration={3000}
            />
        </section>
    );
}
